/* eslint-disable react/prop-types */

import React, { useState, useEffect, useCallback } from 'react';
import { Button, Col, Row, Table } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import DynamicNormalForm from '../dynamicNormalForm';
import { generateValidationSchema } from '../validationSchema';
import DynamicDropdown from './ModalDropdown';
import { MdDelete, MdEdit } from 'react-icons/md';
import { globalValidations } from 'helpers/utils';
import ImageUpload from '../dynamicNormalForm/FormFields/ImageUpload';

export const ModalForm = ({
  modalConfig,
  show,
  onClose,
  onSubmit,
  initialData = [],
  modalFieldOptions = {},
  parentFormValues = {}
}) => {
  const [tableData, setTableData] = useState(initialData);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showModal, setShowModal] = useState(show);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [fieldOptions, setFieldOptions] = useState({});
  const [dependentOptionMap, setDependentOptionMap] = useState({});

  const modalId = modalConfig.id;
  // Create a separate form context for the modal
  const modalMethods = useForm({
    resolver: yupResolver(
      generateValidationSchema({
        fields: modalConfig.fields
      })
    )
  });

  const {
    handleSubmit,
    control,
    register,
    reset,
    formState: { errors },
    setValue
  } = modalMethods;

  // Set initial table data if provided
  useEffect(() => {
    if (initialData?.length > 0) {
      setTableData(initialData);
    }
  }, [initialData]);

  // Handle the show state
  useEffect(() => {
    setShowModal(prev => (prev !== show ? show : prev));
  }, [show]);

  // Reset form when modal closes or submits
  useEffect(() => {
    if (!showModal && editingIndex === null) {
      reset();
    }
  }, [showModal, reset, editingIndex]);

  useEffect(() => {
    const handleFieldReset = event => {
      const { fieldId, value } = event.detail;
      setValue(fieldId, value);
    };

    const eventName = `resetModalField_${modalId}`;
    window.addEventListener(eventName, handleFieldReset);

    return () => {
      window.removeEventListener(eventName, handleFieldReset);
    };
  }, [modalId, setValue]);

  // ADD THIS useEffect FOR UPDATING FIELD OPTIONS
  useEffect(() => {
    Object.entries(modalFieldOptions).forEach(([key, options]) => {
      if (key.startsWith(`${modalId}.`)) {
        const fieldId = key.split('.')[1];
        setFieldOptions(prev => ({
          ...prev,
          [fieldId]: options
        }));
      }
    });
  }, [modalFieldOptions, modalId]);

  useEffect(() => {
    const handleFieldReset = event => {
      const { fieldId, value } = event.detail;
      setValue(fieldId, value);
    };

    const eventName = `resetModalField_${modalId}`;
    window.addEventListener(eventName, handleFieldReset);

    return () => {
      window.removeEventListener(eventName, handleFieldReset);
    };
  }, [modalId, setValue]);

  // ADD THIS useEffect FOR UPDATING FIELD OPTIONS
  useEffect(() => {
    Object.entries(modalFieldOptions).forEach(([key, options]) => {
      if (key.startsWith(`${modalId}.`)) {
        const fieldId = key.split('.')[1];
        setFieldOptions(prev => ({
          ...prev,
          [fieldId]: options
        }));
      }
    });
  }, [modalFieldOptions, modalId]);

  const handleDropdownOptions = useCallback(
    (fieldId, options) => {
      setDropdownOptions(prev => {
        if (JSON.stringify(prev[fieldId]) !== JSON.stringify(options)) {
          const fields = Object.values(modalConfig.fields);
          const currentField = fields.find(f => f.id === fieldId);

          if (currentField?.dependsOn) {
            const parentId = currentField.dependsOn;
            const parentValue = modalMethods.getValues(parentId);

            if (parentValue) {
              setDependentOptionMap(prevMap => ({
                ...prevMap,
                [parentValue]: new Set([
                  ...(prevMap[parentValue] || []),
                  ...options.map(opt => opt.value)
                ])
              }));
            }
          }

          return { ...prev, [fieldId]: options };
        }
        return prev;
      });
    },
    [modalConfig.fields, modalMethods]
  );

  useEffect(() => {
    // Handle edit mode initialization for modal fields
    if (show && initialData?.length > 0 && editingIndex === null) {
      console.log('🔧 Modal opened with initial data in edit mode');

      // Check if any field has dependencies and trigger them
      Object.values(modalConfig.fields).forEach(field => {
        if (field.dependsOn && field.endpoint) {
          // Find the dependent value from the first row of initial data
          const firstRow = initialData[0];
          const dependentValue = firstRow[field.dependsOn];

          if (dependentValue) {
            console.log(
              `🎯 Modal: Found dependent value ${dependentValue} for field ${field.id}`
            );

            // Simulate the parent value change to trigger option loading
            setTimeout(() => {
              // This will trigger the DynamicDropdown's dependency useEffect
              setValue(field.dependsOn, dependentValue);
            }, 100);
          }
        }
      });
    }
  }, [show, initialData, editingIndex, modalConfig.fields, setValue]);

  const handleFormSubmit = async (data, e, keepModalOpen = false) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await modalMethods.trigger();

      if (modalConfig.validation) {
        // if there is global validations if will check for those validations
        const response = globalValidations(data, modalConfig); // function to check the global validations and returns true if all validation passed or return false if validations fails along with a toast message
        if (!response) return;
      }

      if (Object.keys(modalMethods.formState.errors).length === 0) {
        // Process data to include dropdown labels
        // const labelModifiedData = transformData(data);
        const processedData = processFormData(data);
        let updatedTableData;
        if (editingIndex !== null) {
          updatedTableData = [...tableData];
          updatedTableData[editingIndex] = processedData;
          setTableData(updatedTableData);
          setEditingIndex(null);
        } else {
          updatedTableData = [...tableData, processedData];
          setTableData(updatedTableData);
        }

        if (onSubmit) {
          onSubmit({
            data: updatedTableData,
            modalId: modalConfig.id
          });
        }

        reset(); // Reset form fields after submission

        if (!keepModalOpen) {
          setShowModal(false);
          onClose(false); // Close modal only if keepModalOpen is false
        }
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // Process form data to include dropdown labels
  const processFormData = data => {
    const processedData = { ...data };

    // Find dropdown fields and include their labels
    Object.values(modalConfig.fields).forEach(field => {
      if (data[field.id] == null || data[field.id] === '') {
        processedData[field.id] = null;
        processedData[`${field.id}_label`] = null;
      }
      if (field.type === 'dropdown' && data[field.id]) {
        const fieldValue = data[field.id];

        // Find the selected option to get its label
        const options = dropdownOptions[field.id] || [];
        const option = options?.find(opt => opt.value === fieldValue);

        if (option) {
          // Add the label for display purposes
          processedData[`${field.id}_label`] = option.label;
        }
      }
    });

    return processedData;
  };

  const handleEdit = index => {
    setEditingIndex(index);
    const rowData = tableData[index];

    // Reset the form first
    reset();

    // Set values for all fields
    Object.keys(rowData).forEach(key => {
      if (!key.endsWith('_label')) {
        const field = Object.values(modalConfig.fields).find(f => f.id === key);

        if (field?.dependsOn) {
          setTimeout(() => {
            setValue(key, rowData[key]);
          }, 1000);
        } else {
          setValue(key, rowData[key]);
        }
      }
    });

    // Open the modal for editing
    setShowModal(true);
    onClose(true);
  };

  const handleDelete = index => {
    const updatedData = tableData.filter((_, i) => i !== index);
    setTableData(updatedData);
    onSubmit({ data: updatedData, modalId: modalConfig.id });
  };

  // Generic helper to get already selected values for exclusion
  const getExcludeValues = field => {
    if (!field.exclude_selected) return [];

    const allFields = Object.values(modalConfig.fields);
    const fieldId = field.id;

    // ✅ Define isParent and isChild here
    const isParent = field.dependents?.length > 0;
    const parentField = allFields.find(
      f => f.exclude_selected && f.dependents?.includes(fieldId)
    );
    const isChild = !!parentField;

    if (isParent) {
      const parentOptions = dropdownOptions[fieldId] || [];
      const parentToSelectedChildren = {};

      // Collect selected children for each parent
      tableData.forEach((row, idx) => {
        if (idx === editingIndex) return;
        const parentVal = row[fieldId];
        field.dependents.forEach(depId => {
          const childVal = row[depId];
          if (parentVal) {
            if (!parentToSelectedChildren[parentVal]) {
              parentToSelectedChildren[parentVal] = new Set();
            }
            if (childVal) {
              parentToSelectedChildren[parentVal].add(childVal);
            }
          }
        });
      });

      // Add current form values too
      const currentParent = modalMethods.getValues(fieldId);
      field.dependents.forEach(depId => {
        const currentChild = modalMethods.getValues(depId);
        if (currentParent) {
          if (!parentToSelectedChildren[currentParent]) {
            parentToSelectedChildren[currentParent] = new Set();
          }
          if (currentChild) {
            parentToSelectedChildren[currentParent].add(currentChild);
          }
        }
      });

      // Return excluded values
      return parentOptions
        .filter(option => {
          const parentVal = option.value;
          const allChildren = dependentOptionMap?.[parentVal] ?? new Set();
          const selected = parentToSelectedChildren[parentVal];

          // Case 1: parent has children → exclude if all selected
          if (allChildren && allChildren.size > 0) {
            return selected && selected.size >= allChildren.size;
          }

          // Case 2: no children → exclude if selected once
          const selectedOnce = tableData.some(
            (row, idx) => idx !== editingIndex && row[fieldId] === parentVal
          );

          // Also check if current form has same value (to hide it for next row)
          const currentFormVal = modalMethods.getValues(fieldId);
          const isInCurrent = currentFormVal && currentFormVal === parentVal;

          return selectedOnce || isInCurrent;
        })
        .map(option => option.value);
    }

    // ✅ 2. Child (exclude values with same parent)
    if (isChild && parentField) {
      const parentValue = modalMethods.getValues(parentField.id);
      return tableData
        .filter(
          (row, idx) =>
            idx !== editingIndex && row[parentField.id] === parentValue
        )
        .map(row => row[fieldId])
        .filter(Boolean);
    }

    // ✅ Case 3: Independent or parent with no dependents (e.g., Sugarcane)
    const currentValue = modalMethods.getValues(fieldId);
    const excludeSet = new Set();

    // Collect existing values in tableData
    tableData.forEach((row, idx) => {
      if (idx !== editingIndex && row[fieldId]) {
        excludeSet.add(row[fieldId]);
      }
    });

    // Optionally add current value if editing
    if (
      editingIndex === null &&
      currentValue &&
      ![...excludeSet].includes(currentValue)
    ) {
      excludeSet.add(currentValue);
    }

    return Array.from(excludeSet);
  };

  const renderField = useCallback(
    field => {
      // const isFirstRow = tableData.length === 0 || editingIndex === 0;
      const isEditing = editingIndex !== null;
      // Get external options for cross-component dependencies
      const externalOptions = fieldOptions[field.id] || null;

      // console.log(`🔍 Rendering field ${field.id}:`, {
      //   hasExternalOptions: !!externalOptions,
      //   externalOptionsCount: externalOptions?.length || 0,
      //   fieldType: field.type
      // });

      if (field.type === 'dropdown') {
        let excludeValues = getExcludeValues(field);

        // Only for crop/crop-variety fields, allow current value in edit mode
        if (isEditing && field.exclude_selected) {
          const currentValue =
            tableData[editingIndex] && tableData[editingIndex][field.id];
          if (
            currentValue !== undefined &&
            currentValue !== null &&
            excludeValues.includes(currentValue)
          ) {
            excludeValues = excludeValues.filter(val => val !== currentValue);
          }
        }

        return (
          <DynamicDropdown
            key={`${field.id}-${
              externalOptions?.length || field.options?.length || 0
            }`}
            metadata={{ ...field }}
            setDropdownOptions={handleDropdownOptions}
            externalOptions={externalOptions} // NEW: Pass external options
            excludeValues={excludeValues}
          />
        );
      }

      if (field.type === 'file') {
        const currentValue = modalMethods.watch(field.id);
        return (
          <ImageUpload
            key={field.id}
            metadata={{
              ...field,
              id: field.id,
              defaultValue:
                editingIndex !== null
                  ? tableData[editingIndex]?.[field.id]
                  : undefined
            }}
            setValue={setValue}
            value={currentValue}
          />
        );
      }

      return (
        <DynamicNormalForm
          key={field.id}
          fields={modalConfig.fields}
          metadata={field}
          control={control}
          mode="create"
          register={register}
          errors={errors}
        />
      );
    },
    [
      modalConfig.fields,
      control,
      register,
      errors,
      handleDropdownOptions,
      tableData.length,
      editingIndex,
      setValue,
      modalMethods,
      fieldOptions, // CRITICAL: This ensures re-render when options change
      parentFormValues,
      modalMethods,
      getExcludeValues
    ]
  );

  const renderFields = () => {
    return Object.values(modalConfig.fields).map(field => (
      <Col
        lg={field.type === 'textarea' ? 12 : field.width ? field.width : 6}
        key={field.id}
        className="g-lg-4 g-2"
      >
        {renderField(field)}
      </Col>
    ));
  };

  const renderTable = () => {
    const displayFields = Object.values(modalConfig.fields).filter(
      field => !field.hidden
    );
    return (
      <div className="mt-4 overflow-auto">
        <Table striped bordered hover>
          <thead>
            <tr>
              {displayFields.map(field => (
                <th key={field.id} style={{ fontSize: '0.75rem' }}>
                  {field.label}
                </th>
              ))}
              <th style={{ fontSize: '0.75rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length === 0 ? (
              <tr>
                {displayFields.map(field => (
                  <td key={field.id}>-</td>
                ))}
                <td>-</td>
              </tr>
            ) : (
              tableData.map((row, index) => (
                <tr key={index}>
                  {displayFields.map(field => (
                    <td key={field.id}>
                      {field.type === 'file' ? (
                        row[field.id] ? (
                          <img
                            src={row[field.id]} // Use the uploaded URL from the table data
                            alt="Uploaded"
                            style={{
                              position: 'relative',
                              width: '36px',
                              height: '30px',
                              marginLeft: '28px',
                              borderRadius: '4px'
                            }}
                          />
                        ) : (
                          '-'
                        )
                      ) : field.type === 'dropdown' ? (
                        row[`${field.id}_label`] || '-'
                      ) : field.type === 'minmax' &&
                        row[field.id] &&
                        typeof row[field.id] === 'object' ? (
                        row[field.id]?.min !== null &&
                        row[field.id]?.max !== null ? (
                          `${row[field.id].min}-${row[field.id].max}`
                        ) : (
                          '-'
                        )
                      ) : (
                        row[field.id] || '-'
                      )}
                    </td>
                  ))}
                  <td>
                    <span
                      onClick={() => handleEdit(index)}
                      style={{
                        cursor: 'pointer',
                        marginRight: '8px',
                        color: 'green'
                      }}
                    >
                      <MdEdit size={20} />
                    </span>
                    <span
                      onClick={() => handleDelete(index)}
                      style={{ cursor: 'pointer', color: '#C00000' }}
                    >
                      <MdDelete size={20} />
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  const handleModalClose = e => {
    // Prevent event from bubbling up to parent form
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    reset();
    setEditingIndex(null);
    setShowModal(false);
    onClose(false);
  };

  return (
    <>
      {/* Modal */}
      <div
        className={`modal ${showModal ? 'show d-block' : ''}`}
        tabIndex="-1"
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {editingIndex !== null ? 'Edit Entry' : modalConfig.form.title}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleModalClose}
              ></button>
            </div>
            <div className="modal-body">
              {/* Create a new FormProvider context for the modal */}
              <FormProvider {...modalMethods}>
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit(handleFormSubmit)(e);
                  }}
                  noValidate
                  onClick={e => e.stopPropagation()}
                >
                  <Row className="gx-2 gy-3">{renderFields()}</Row>
                  <div className="text-end mt-3">
                    <Button
                      variant="secondary"
                      onClick={handleModalClose}
                      className="me-2"
                    >
                      Cancel
                    </Button>
                    {/* Submit & Close Button */}
                    <Button
                      type="submit"
                      variant="primary"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmit(data => handleFormSubmit(data, e, false))(
                          e
                        );
                      }}
                    >
                      {editingIndex !== null ? 'Update' : 'Submit'}
                    </Button>

                    {/* Submit & Add Another Button */}
                    <Button
                      type="button"
                      variant="success"
                      className="ms-2"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmit(data => handleFormSubmit(data, e, true))(
                          e
                        );
                      }}
                    >
                      Submit & Add Another
                    </Button>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
      {showModal && <div className="modal-backdrop show"></div>}

      {/* Table */}
      {renderTable()}
    </>
  );
};
